/**
 * Tier 1 self-help: when an error is a known help-desk problem, the app tells the person how
 * to fix it themselves instead of an admin being alerted. Spec:
 * docs/superpowers/specs/2026-10-23-tier1-self-help-errors-design.md
 *
 * Two fixes: "update" (they're on an old copy and a newer one is live) and "signin" (the app
 * lost track of who they are). When unsure, it's neither — the error goes to the Error Log
 * like any other.
 *
 * Module-level store (same pattern as updateStatus): logError offers, SelfHelpSheet shows.
 */
import type { ErrorLog } from '../types';
import { isNewerVersion } from './updateStatus';

export type SelfHelp = 'update' | 'signin';

/** "Remind me in a few minutes" keeps the update pop-up away this long. */
export const REMIND_AFTER_MS = 5 * 60 * 1000;

const PENDING_KEY = 'selfHelp.pendingSignin';

const SIGNIN_CODES = new Set([
  'auth/user-token-expired',
  'auth/id-token-expired',
  'auth/user-token-revoked',
  'unauthenticated',
  'functions/unauthenticated',
]);
const PERMISSION_CODES = new Set(['permission-denied', 'firestore/permission-denied']);

const codeOf = (error: unknown): string =>
  error && typeof error === 'object' && typeof (error as { code?: unknown }).code === 'string'
    ? (error as { code: string }).code
    : '';

/**
 * Which fix to offer, or null. A permission error while signed in is usually a real rules
 * bug, so it only counts as a sign-in problem when the app has no signed-in user.
 */
export function classifySelfHelp(
  error: unknown,
  type: ErrorLog['type'],
  ctx: { running: string; live: string | null; signedIn: boolean },
): SelfHelp | null {
  if (type === 'user_report') return null;
  const code = codeOf(error);
  if (SIGNIN_CODES.has(code) || (PERMISSION_CODES.has(code) && !ctx.signedIn)) return 'signin';
  if (isNewerVersion(ctx.live, ctx.running)) return 'update';
  return null;
}

let offer: SelfHelp | null = null;
let snoozedAt: number | null = null;
let remindTimer: ReturnType<typeof setTimeout> | null = null;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());

export const subscribeSelfHelp = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const getSelfHelpOffer = (): SelfHelp | null => offer;

/** One pop-up at a time; a snoozed update stays away for REMIND_AFTER_MS. */
export function offerSelfHelp(kind: SelfHelp, nowMs: number = Date.now()): void {
  if (offer) return;
  if (kind === 'update' && snoozedAt !== null && nowMs - snoozedAt < REMIND_AFTER_MS) return;
  offer = kind;
  notify();
}

/**
 * "Remind me in a few minutes". After the wait the update pop-up comes back on its own if
 * `stillOld` says they're still on the old copy — it doesn't wait for another error.
 */
export function snoozeSelfHelp(nowMs: number = Date.now(), stillOld?: () => boolean): void {
  snoozedAt = nowMs;
  offer = null;
  notify();
  if (remindTimer) clearTimeout(remindTimer);
  remindTimer = stillOld
    ? setTimeout(() => { remindTimer = null; if (stillOld()) offerSelfHelp('update', nowMs + REMIND_AFTER_MS); }, REMIND_AFTER_MS)
    : null;
}

export function dismissSelfHelp(): void {
  offer = null;
  notify();
}

type PendingEntry = Omit<ErrorLog, 'id'>;

/** A sign-in error can't be logged while signed out; keep it until they sign back in. */
export function rememberSigninError(entry: PendingEntry): void {
  try {
    localStorage.setItem(PENDING_KEY, JSON.stringify(entry));
  } catch {
    // Storage blocked — the pop-up still shows, only the Error Log record is lost.
  }
}

export function takePendingSigninError(): PendingEntry | null {
  try {
    const raw = localStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    localStorage.removeItem(PENDING_KEY);
    return JSON.parse(raw) as PendingEntry;
  } catch {
    return null;
  }
}

export const _resetSelfHelpForTests = (): void => {
  offer = null;
  snoozedAt = null;
  if (remindTimer) clearTimeout(remindTimer);
  remindTimer = null;
  try { localStorage.removeItem(PENDING_KEY); } catch { /* ignore */ }
};
